import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ISbRichtext, ISbStoryData } from '@storyblok/js';
import { PICFLOW_ANALYTICS } from 'apps/picflow/src/app/picflow-analytics';
import { PICFLOW_STORYBLOK } from 'apps/picflow/src/app/picflow-storyblok';
import { filter, map, merge, of, switchMap } from 'rxjs';
import { RatingComponent } from '../../../../storyblok/components/rating/rating.component';
import { StoryblokRootComponent } from '../../../../storyblok/components/storyblok-root/storyblok-root.component';
import { StoryblokImage } from '../../../../storyblok/storyblok-types';
import { getFormatsFromFormatParam } from '../../get-formats-from-format-param';

export type ConversionFormat = ISbStoryData<{
  name: string;
  category: 'raw' | 'general';
  subCategory: 'lossy' | 'lossless' | 'vector';
  longDescription: ISbRichtext;
  shortDescription: string;
  sourceOnly: boolean;
  defaultIcon: StoryblokImage;
  uploadIcon: StoryblokImage;
  downloadIcon: StoryblokImage;
  mainVisual: StoryblokImage;
  isPopular: boolean;
  _editable: string;
}>;

@Component({
  selector: 'app-conversion-template',
  templateUrl: './conversion-template.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, StoryblokRootComponent, RatingComponent],
})
export class ConversionTemplateComponent {
  private route = inject(ActivatedRoute);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private analytics = inject(PICFLOW_ANALYTICS);
  fromFormat: ConversionFormat;
  toFormat: ConversionFormat;
  story$ = merge(this.route.data, this.storyblokService.updates$.pipe(filter(Boolean))).pipe(map(data => data?.story));
  blurOverlay$ = this.story$.pipe(map(story => story && story.content.blurOverlay));
  hasHomeTeaser$ = this.story$.pipe(map(story => story && story.content.body?.[0]?.component === 'HomeTeaser'));
  prefooter$ = this.story$.pipe(map(story => story && story.content.preFooter?.[0]));
  topBlur$ = this.story$.pipe(map(story => story && story.content.topBlur));
  topBlurMaxHeight$ = this.story$.pipe(map(story => story && story.content.topBlurMaxHeight));
  verticalDottedLines$ = this.story$.pipe(map(story => story && story.content.verticalDottedLines));
  darkHeader$ = this.hasHomeTeaser$.pipe(
    switchMap(hasHomeTeaser => {
      if (hasHomeTeaser) return of(true);
      return this.story$.pipe(map(story => ['dark', 'black'].includes(story && story.content.background)));
    })
  );

  extraDataMap$ = this.route.data.pipe(
    switchMap(data =>
      this.formats$.pipe(
        map(formats => {
          const map = new Map<string, { [key: string]: unknown }>();
          map.set('ConvertToFormatList', {
            formats: (data.formats as ConversionFormat[]).filter(
              f => !f.content.sourceOnly && f.slug !== formats.from?.slug
            ),
            fromFormat: formats.from && (data.formats as ConversionFormat[]).find(f => f.slug === formats.from.slug),
          });
          map.set('Conversion', {
            fromFormat: formats.from && (data.formats as ConversionFormat[]).find(f => f.slug === formats.from.slug),
            toFormat: formats.to && (data.formats as ConversionFormat[]).find(f => f.slug === formats.to.slug),
          });
          return map;
        })
      )
    )
  );

  private formats$ = this.route.params.pipe(
    switchMap(params =>
      this.route.data.pipe(
        map(data => {
          const { from: fromFormat, to: toFormat } = getFormatsFromFormatParam(params.format);
          const formats = data.formats as ConversionFormat[];
          const from = formats.find((format: typeof this.toFormat) => format.slug === fromFormat);
          const to = formats.find((format: typeof this.toFormat) => format.slug === toFormat);
          return { from, to };
        })
      )
    )
  );

  private getFormats$ = this.formats$.subscribe(formats => {
    this.fromFormat = formats.from;
    this.toFormat = formats.to;
  });

  openIntercom() {
    this.analytics.showIntercom();
  }
}
